/*
 * <delivery-calculator> (snippets/delivery-calculator.liquid): the shopper's Finnish postcode in, the
 * delivery prices for its driving distance from Helsinki out, under the product page's delivery line and
 * on its Delivery tab.
 *
 * - The table (assets/postal-distances.json: every postal code area's municipality and its driving
 *   distance from central Helsinki) loads when the field is first focused, so it costs the page
 *   nothing. Built from Statistics Finland's postal code areas (Paavo, CC BY 4.0) and road distances
 *   from OpenStreetMap data (© OpenStreetMap contributors, ODbL) by the OSRM router. A postcode the
 *   table doesn't have (a PO box, a company's own code) takes the area of the nearest code that shares
 *   its first three digits, or two.
 * - The prices are in the markup, one row per zone (Theme settings > Delivery prices); the script only
 *   picks the zone: near, middle, or far by the km limits, or Åland (22xxx), and shows the parts whose
 *   `data-for` names it.
 * - The postcode is remembered (localStorage), so every product page shows its prices from the start:
 *   the snippet's inline script shows them before the first paint, and this script checks the zone
 *   against the current limits.
 */

const STORAGE_KEY = 'delivery-postcode';
let table = null;

function loadTable(url) {
  if (!table) {
    table = fetch(url).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    });
    // A failed load is tried again by the next postcode.
    table.catch(() => (table = null));
  }
  return table;
}

function lookup({ names, codes }, code) {
  let area = codes[code];
  for (const length of [3, 2]) {
    if (area) break;
    const prefix = code.slice(0, length);
    let gap = Infinity;
    for (const [key, value] of Object.entries(codes)) {
      const distance = Math.abs(Number(key) - Number(code));
      if (key.startsWith(prefix) && distance < gap) [area, gap] = [value, distance];
    }
  }
  return area ? { code, town: names[area[0]], km: area[1] } : null;
}

function read() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function write(entry) {
  try {
    if (entry) localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // No storage: the postcode is asked again on the next page.
  }
}

class DeliveryCalculator extends HTMLElement {
  #abort;
  #run = 0;

  connectedCallback() {
    this.input = this.querySelector('input');
    this.result = this.querySelector('[data-result]');
    if (!this.input || !this.result) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.input.addEventListener('focus', () => loadTable(this.dataset.table).catch(() => {}), { signal });
    this.input.addEventListener(
      'input',
      () => {
        // Digits only, at most five; the fifth shows the prices at once, an empty field clears them.
        const digits = this.input.value.replace(/\D/g, '').slice(0, 5);
        if (digits !== this.input.value) this.input.value = digits;
        if (digits.length === 5) {
          this.#calculate(digits);
        } else if (!digits) {
          this.#run++;
          this.result.hidden = true;
          delete this.dataset.zone;
          write(null);
        }
      },
      { signal }
    );
    this.querySelector('form')?.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        this.#calculate(this.input.value.trim());
      },
      { signal }
    );

    // Already shown by the snippet's inline script; its zone follows the current limits.
    const saved = read();
    if (saved?.code && Number.isFinite(saved.km)) {
      const state = this.#show(saved);
      if (state !== saved.state) write({ ...saved, state });
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  async #calculate(code) {
    const run = ++this.#run;
    if (!/^\d{5}$/.test(code)) {
      this.#setState('invalid');
      return;
    }

    this.setAttribute('aria-busy', 'true');
    try {
      const entry = lookup(await loadTable(this.dataset.table), code);
      if (run !== this.#run) return;
      if (entry) write({ ...entry, state: this.#show(entry) });
      else this.#setState('not-found');
    } catch {
      if (run === this.#run) this.#setState('error');
    } finally {
      if (run === this.#run) this.removeAttribute('aria-busy');
    }
  }

  #zone({ code, km }) {
    if (code.startsWith('22')) return 'aland';
    if (km <= Number(this.dataset.nearKm)) return '1';
    return km <= Number(this.dataset.middleKm) ? '2' : '3';
  }

  #show(entry) {
    for (const key of ['code', 'town', 'km']) {
      const slot = this.querySelector(`[data-${key}]`);
      if (slot) slot.textContent = entry[key];
    }
    const state = this.#zone(entry);
    this.#setState(state);
    return state;
  }

  #setState(state) {
    for (const part of this.querySelectorAll('[data-for]')) {
      part.hidden = !part.dataset.for.split(' ').includes(state);
    }
    // The zone, for styles beside it: the Delivery tab highlights its column of the price table.
    if (['1', '2', '3', 'aland'].includes(state)) this.dataset.zone = state;
    else delete this.dataset.zone;
    this.result.hidden = false;
  }
}

if (!customElements.get('delivery-calculator')) customElements.define('delivery-calculator', DeliveryCalculator);
